import { useTranslation } from 'next-i18next';
import { memo } from 'react';
import ComponentWrapper from '../../common/ContentWrapper';
import ImageWithFallback from '../../common/ImageWithFallback';
import type CampaignBannerType from '../../../../types/CampaignBanner';
import Carousel from '../../common/Carousel';
import { PromotionType } from '../../../core/constants';
import analytics from '../../../core/analytics/analytics';

type Props = {
  campaignBanners: CampaignBannerType[];
};

const CampaignBanner = ({ campaignBanners }: Props) => {
  const { t } = useTranslation('home');
  const maxSlidesToShow = 3;
  const numOfCampaignBanners = campaignBanners.length || 0;
  const settings = {
    className: '-mx-2', // remove spaces at the far left/right ends from slider container
    cardSpacing: 'px-2', // add spaces at both of the sides of cards
    infinite: numOfCampaignBanners > maxSlidesToShow,
    slidesToShow: 3,
    draggable: false,
    prevBtnStyle: '-left-3 card-carousel-arrow-btn',
    nextBtnStyle: '-right-3 card-carousel-arrow-btn',
    prevArrowStyle: 'stroke-2',
    nextArrowStyle: 'stroke-2',
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.1,
          infinite: false,
          arrows: false,
          draggable: true,
        },
      },
    ],
  };

  const events = {
    onClick: (banner: CampaignBannerType) => {
      analytics.google.ecommerce.track.promotion.view(
        {
          promotion_name: PromotionType.CAMPAIGN_BANNER,
          promotion_id: banner?.url || '',
        },
        [
          {
            item_id: banner?.url || '',
            item_name: banner?.image?.altText || '',
          },
        ]
      );
    },
  };

  return (
    <ComponentWrapper className="pt-12 sm:pt-16">
      <h2 className="app-section-title">{t('campaign.campaign_title')}</h2>

      <div className="-mr-2 sm:mr-0">
        <Carousel
          settings={settings}
          renderItem={(banner, prefetchImages) => {
            // TODO: investigate why prefetching only next one image doesn't improve the scrolling behavior
            return (
              <ImageWithFallback
                alt={banner.image?.altText || 'campaign banner'}
                key={banner.id}
                className="rounded-xl"
                src={banner.image?.url || ''}
                url={banner.url}
                layout="responsive"
                width={384}
                height={240}
                fallbackSrc="/fallback-img-790x394.png"
                sizes="(max-width: 768px) 50vw, 33vw"
                onClick={() => events.onClick(banner)}
                loading={prefetchImages ? 'eager' : 'lazy'}
              />
            );
          }}
          items={campaignBanners}
        />
      </div>
    </ComponentWrapper>
  );
};

export default memo(CampaignBanner);
